/* eslint semi: 1 */
import { computed, ref, watch } from 'vue';
import { useMediaQuery } from '@/composables/media/use-media-query';
import CONST from '@/globals/constants';
import type { MediaExtendedSortOrder } from '@/pinia/stores/media/mediaQueryStore';

interface SortOption {
  label: string;
  i18n: string;
  value: MediaExtendedSortOrder | null;
}

interface SortGroupBase {
  key: string;
  label: string;
  i18n: string;
  type: 'multi-select' | 'single-select'
  options: SortOption[];
}

interface SingleSelectSortGroup extends SortGroupBase {
  type: 'single-select';
  value: MediaExtendedSortOrder | null;
}

type SortGroup = SingleSelectSortGroup;

/**
 * All valid `sortOrder` values accepted by the `GET /media` API.
 *
 * Format: `direction`
 * - `desc`
 * - `asc`
 */
export const SORT_ORDER_OPTIONS: SortOption[] = [
  {
    label: 'Last Modified (Newest First)',
    i18n: 'label.lastModifiedDesc',
    value: CONST.MEDIA_SORT_DESC
  },
  {
    label: 'Last Modified (Oldest First)',
    i18n: 'label.lastModifiedAsc',
    value: CONST.MEDIA_SORT_ASC
  },
  {
    label: 'Name (A → Z)',
    i18n: 'label.nameAsc',
    value: CONST.MEDIA_SORT_NAME_ASC
  },
  {
    label: 'Name (Z → A)',
    i18n: 'label.nameDesc',
    value: CONST.MEDIA_SORT_NAME_DESC
  }
];

/**
 * Default schema for the sort dropdown.
 */
export const MEDIA_SORT_DEFAULT_SCHEMA: readonly SortGroup[] = Object.freeze([
  {
    key: 'sortOrder',
    label: 'Sort by',
    i18n: 'label.sortBy',
    type: 'single-select',
    options: SORT_ORDER_OPTIONS,
    value: CONST.SORT_ORDER_DEFAULT
  }
]);

/**
 * Composable for the `MediaSort` dropdown component.
 *
 * Manages the **draft** sort selection before the user
 * clicks "Apply". Bridges to `useMediaQuery` for persistence
 * via `$filtersRepository` and URL.
 */
export function useMediaSorting () {
  const query = useMediaQuery();
  const schema = ref<SortGroup[]>(buildSchema());

  // Sync draft whenever the committed sort changes externally
  // (page init, URL navigation, clearAll, etc.)
  watch(
    query.sortOrder,
    () => { initialize(); },
    { immediate: true }
  );

  /**
   * True when the committed sort differs from the API default.
   * Use this for the trigger button highlight / badge.
   */
  const isNonDefault = computed(() => query.sortOrder.value !== CONST.SORT_ORDER_DEFAULT);

  /**
   * Updates a group's draft value.
   * @param {string} key — group key (e.g. `'sortOrder'`)
   * @param {MediaExtendedSortOrder | null} value — one of the `SORT_ORDER_OPTIONS` values
   */
  function setGroupValue (key: string, value: MediaExtendedSortOrder | null) {
    const group = schema.value.find(g => g.key === key);
    if (group) group.value = value;
  }

  /**
   * Seeds the draft schema from the current committed query state.
   */
  function initialize () {
    schema.value = buildSchema({ sortOrder: query.sortOrder.value });
  }

  /**
   * Commits the current draft sort to the query layer and persists to URL.
   */
  function apply () {
    const group = schema.value.find(g => g.key === 'sortOrder');
    query.applySortOrder(group?.value ?? CONST.SORT_ORDER_DEFAULT);
  }

  /**
   * Resets the draft back to the API default and clears
   * the committed sort from the query and persists to URL.
   */
  function reset () {
    schema.value = buildSchema();
    query.clearSortOrder();
  }

  return {
    schema,
    isNonDefault,
    // Mutations
    setGroupValue,
    // Query bridge
    initialize,
    apply,
    reset
  };
}

// Internal helpers
function buildSchema (overrides: Partial<Record<string, unknown>> = {}): SortGroup[] {
  return MEDIA_SORT_DEFAULT_SCHEMA.map(group => ({
    ...group,
    options: [...group.options],
    value: Object.prototype.hasOwnProperty.call(overrides, group.key)
      ? overrides[group.key]
      : group.value
  })) as SortGroup[];
}
